import type { ReactNode } from 'react';
import { Typography } from 'antd';

const { Title, Paragraph } = Typography;

export interface PageHeaderProps {
  title: string;
  /** The surface's own icon — the same glyph the nav uses, so the two agree. */
  icon?: ReactNode;
  /** One or two lines: what this page is for. Kept to a readable measure, never full-bleed. */
  subtitle?: ReactNode;
  /** Right-hand controls (refresh, primary action, range picker…). */
  extra?: ReactNode;
}

/**
 * The one page header (OPSFL-POLISH-1 density pass). Every list surface used to hand-roll its
 * title block, which drifted: three different top paddings, two title margins, subtitles between
 * 680 and 800px, some pages with no title at all. An operator moving between surfaces at speed
 * reads position before content — a title that shifts by 4px between pages costs a re-fixation
 * every single time.
 *
 * Deliberately NOT AntD's PageHeader (removed in v5) and deliberately not restyling the theme
 * tokens: this composes the existing Typography scale into one fixed rhythm.
 */
export function PageHeader({ title, icon, subtitle, extra }: PageHeaderProps) {
  return (
    <div className="ol-page-header">
      <div className="ol-page-header__text">
        <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>
          {icon && <span style={{ marginInlineEnd: 10, color: 'var(--ol-primary)' }}>{icon}</span>}
          {title}
        </Title>
        {subtitle && (
          <Paragraph type="secondary" style={{ marginBottom: 0, maxWidth: 780 }}>
            {subtitle}
          </Paragraph>
        )}
      </div>
      {extra && <div className="ol-page-header__extra">{extra}</div>}
    </div>
  );
}
